/**
 * StorePreferencesPage — the "Store preferences" surface (T12.2 / 📍).
 *
 * The home for ALL store management (relocated here from App settings, T12.2
 * step 9). Two stacked concerns:
 *
 *   1. The household store list — select / deselect, favourite ★, edit ✎,
 *      remove ×, "Add online store", and "Find nearby stores" (the discovery
 *      checklist). This is the existing `StoresCard`; the per-store select
 *      control now toggles household selection through the real write path
 *      (`useSetSelectedStores`).
 *   2. Per-store category affinity — for each *selected* store, the category
 *      chips toggled per store (a soft weight, not a rule). Reads the full
 *      preference entries (`GET /store-preferences`, which carries the declared
 *      `categories`) cross-referenced with the store list for names; writes one
 *      store's category set through `PUT /selected-stores/:storeId/categories`.
 *
 * The prototype's "💡 learned" derivation line is out of scope (T12.5); only the
 * static "declared now, learnable later" hint ships.
 */

import { useMemo, useState } from 'react';
import {
  useStorePreferences,
  useStores,
  useHouseholds,
  useSetStoreCategories,
} from '@shopista/shared/api/hooks';
import { apiErrorMessage } from '@shopista/shared/api';
import { Spinner } from '../../components';
import { SettingsLayover } from './SettingsLayover';
import { StoresCard } from './cards';
import {
  setgroupStyle,
  h4Style,
  hintStyle,
  tagsRowStyle,
  TagToggle,
} from './prefControls';

// The category vocabulary shown as per-store chips. Declared signal, free-form-
// tolerant on the backend (categories is a string array inside the store-prefs
// jsonb); this is the curated starter set from the prototype.
const STORE_CATEGORY_OPTIONS = [
  'Produce',
  'Dairy',
  'Bakery',
  'Meat',
  'Pantry',
  'Frozen',
] as const;

const errorStyle = {
  color: 'var(--color-status-danger)',
  fontSize: 'var(--font-size-sm)',
} as const;

const mutedStyle = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
} as const;

export function StorePreferencesPage(): JSX.Element {
  const prefsQuery = useStorePreferences();
  const storesQuery = useStores();
  const householdsQuery = useHouseholds();
  const setCategories = useSetStoreCategories();
  const [actionError, setActionError] = useState<string | null>(null);

  const currentMembership = (householdsQuery.data ?? []).find((h) => h.is_current);
  const isManager = currentMembership?.role === 'manager';

  const selected = prefsQuery.data?.selected ?? [];
  const stores = storesQuery.data?.stores ?? [];

  const storeName = useMemo(() => {
    const map = new Map(stores.map((s) => [s.id, s.name]));
    return (id: string) => map.get(id) ?? id;
  }, [stores]);

  const run = async (fn: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Could not save the categories.'));
    }
  };

  const toggleCategory = (
    storeId: string,
    current: string[],
    category: string
  ) => {
    const next = current.includes(category)
      ? current.filter((c) => c !== category)
      : [...current, category];
    void run(() => setCategories.mutateAsync({ storeId, categories: next }));
  };

  const band = {
    variant: 'steers' as const,
    children: (
      <>
        What you tend to buy <strong>where</strong>. When you plan a trip to a
        store, we surface the categories you usually pick up there. A{' '}
        <strong>weight</strong>, not a rule — you can grab anything anywhere.
      </>
    ),
  };

  return (
    <SettingsLayover
      icon="pin"
      title="Store preferences"
      backLabel="Back"
      testId="settings-stores-pref"
      band={band}
    >
      {/* The household store list lives here now — select / favourite / edit /
          remove / add online / find nearby. */}
      <StoresCard
        isManager={isManager}
        discoveredFromLocationSave={null}
        onConsumeDiscoveredFromLocationSave={() => undefined}
      />

      {actionError && (
        <div role="alert" style={errorStyle} data-testid="settings-stores-pref-error">
          {actionError}
        </div>
      )}

      {prefsQuery.isLoading ? (
        <Spinner />
      ) : selected.length === 0 ? (
        <section style={setgroupStyle} data-testid="settings-stores-pref-empty">
          <h4 style={h4Style}>No stores selected yet</h4>
          <p style={hintStyle}>
            Select the stores you shop at in the list above, then tell us what
            you tend to buy at each one.
          </p>
        </section>
      ) : (
        selected.map((entry) => {
          const current = entry.categories ?? [];
          return (
            <section
              key={entry.storelocation_id}
              style={setgroupStyle}
              data-testid={`settings-stores-pref-${entry.storelocation_id}`}
            >
              <h4 style={h4Style}>{storeName(entry.storelocation_id)}</h4>
              <p style={hintStyle}>Categories you usually buy here. Tap to toggle.</p>
              <div style={tagsRowStyle}>
                {STORE_CATEGORY_OPTIONS.map((cat) => (
                  <TagToggle
                    key={cat}
                    label={cat}
                    on={current.includes(cat)}
                    onToggle={() =>
                      toggleCategory(entry.storelocation_id, current, cat)
                    }
                    testId={`settings-stores-pref-${entry.storelocation_id}-${cat.toLowerCase()}`}
                  />
                ))}
              </div>
            </section>
          );
        })
      )}

      <p style={{ ...mutedStyle, margin: '0 var(--space-1)' }} data-testid="settings-stores-pref-hint">
        Declared now, learnable later: tell us, or let the app pick it up from
        what you actually buy where.
      </p>
    </SettingsLayover>
  );
}
